import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Replay as RevokeIcon,
  Archive as ArchiveIcon,
  MoreTime as ExtendIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { DetailPageHeader } from '../../components/common/DetailPageLayout';
import useDocumentTypes, { labelForDocumentType } from '../../hooks/useDocumentTypes';

const QUEUE = {
  'contact-deletion': {
    title: 'Contact deletion',
    description: 'Contacts scheduled for permanent removal after the retention period.',
    listUrl: '/admin/deletion-queue/contacts',
    entity: 'contacts',
    label: (r) => [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email,
  },
  'account-deletion': {
    title: 'Account deletion',
    description: 'Company accounts scheduled for permanent removal after the retention period.',
    listUrl: '/admin/deletion-queue/accounts',
    entity: 'accounts',
    label: (r) => r.company_name || r.account_code,
  },
  'document-deletion': {
    title: 'Document deletion',
    description: 'Documents (files) scheduled for permanent removal after the retention period.',
    listUrl: '/admin/deletion-queue/documents',
    entity: 'documents',
    label: (r) => r.title || r.file_name,
  },
};

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function DeletionShortView({ row, cfg, isDocumentQueue, documentTypes = [] }) {
  if (!row) return null;
  const ref =
    cfg.entity === 'contacts'
      ? row.contact_code || row.id
      : cfg.entity === 'accounts'
        ? row.account_code || row.id
        : row.document_code || row.id;
  const markedBy = row.deletionMarker
    ? `${row.deletionMarker.first_name || ''} ${row.deletionMarker.last_name || ''}`.trim() || row.deletionMarker.email
    : '—';
  const rows = [
    { k: isDocumentQueue ? 'Title' : 'Name', v: cfg.label(row) },
    { k: 'Reference', v: String(ref) },
    ...(isDocumentQueue
      ? [
          { k: 'Type', v: row.document_type ? labelForDocumentType(row.document_type, documentTypes) : '—' },
          { k: 'File', v: row.file_name || '—' },
        ]
      : []),
    { k: 'Marked at', v: formatWhen(row.deletion_marked_at) },
    { k: 'Scheduled purge', v: formatWhen(row.deletion_scheduled_for) },
    { k: 'Marked by', v: markedBy },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Short view
      </Typography>
      <Stack spacing={0.75}>
        {rows.map(({ k, v }) => (
          <Stack key={k} direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
              {k}
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
              {v}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

export default function AdminDeletionQueue() {
  const { queueType } = useParams();
  const navigate = useNavigate();
  const cfg = QUEUE[queueType];

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [extendOpen, setExtendOpen] = useState({ open: false, row: null });
  const [extendDays, setExtendDays] = useState(7);
  const [actionConfirm, setActionConfirm] = useState({ open: false, row: null, action: null });
  const { documentTypes } = useDocumentTypes();

  const load = useCallback(async () => {
    if (!cfg) return;
    setLoading(true);
    try {
      const r = await api.get(cfg.listUrl, { params: { page: 1, limit: 200 } });
      setRows(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load deletion queue');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [cfg]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!QUEUE[queueType]) {
      navigate('/admin/settings', { replace: true });
    }
  }, [queueType, navigate]);

  if (!cfg) {
    return null;
  }

  const isDocumentQueue = cfg.entity === 'documents';

  const postAction = async (row, action) => {
    const id = row.id;
    setBusyId(id);
    try {
      await api.post(`/admin/deletion-queue/${cfg.entity}/${id}/${action}`, {});
      toast.success(
        action === 'revoke'
          ? 'Deletion revoked.'
          : action === 'archive'
            ? 'Moved to Archived (removed from deletion queue).'
            : 'Updated.'
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const submitExtend = async () => {
    const row = extendOpen.row;
    if (!row) return;
    const d = Math.max(1, Math.min(3650, parseInt(String(extendDays), 10) || 7));
    setBusyId(row.id);
    try {
      await api.post(`/admin/deletion-queue/${cfg.entity}/${row.id}/extend`, { additional_days: d });
      toast.success(`Schedule extended by ${d} day(s).`);
      setExtendOpen({ open: false, row: null });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to extend');
    } finally {
      setBusyId(null);
    }
  };

  const openRecord = (row) => {
    if (cfg.entity === 'contacts') navigate(`/contacts/${row.id}`);
    else if (cfg.entity === 'accounts') navigate(`/accounts/${row.id}`);
    else navigate('/documents?lifecycle=deleted');
  };

  return (
    <Box>
      <DetailPageHeader
        title={cfg.title}
        onBack={() => navigate('/admin/settings')}
        actions={
          <Button variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Alert severity="info">
              {cfg.description} <strong>Marked</strong> is when deletion was requested; <strong>Scheduled purge</strong>{' '}
              is when the record will be permanently removed (subject to automated purge). Use{' '}
              <strong>Revoke</strong> and <strong>Archive</strong> open a <strong>short view</strong> of the record first; confirm in the dialog to apply the action. Use{' '}
              <strong>Revoke</strong> to restore to active, <strong>Archive</strong> to move into the{' '}
              <strong>Archived</strong> bucket (cancel purge, set archive timestamp — eligible for backup zips only from Archived
              Data), or <strong>Extend</strong> to postpone purge.
              {isDocumentQueue ? (
                <>
                  {' '}
                  Marked documents also appear on <strong>Documents</strong> with lifecycle <strong>Deleted</strong> until
                  archived.
                </>
              ) : cfg.entity === 'contacts' ? (
                <>
                  {' '}
                  Deleted contacts also appear under <strong>Data Administration → Deleted Data</strong> until archived.
                </>
              ) : (
                <>
                  {' '}
                  Deleted accounts also appear under <strong>Data Administration → Deleted Data</strong> until archived.
                </>
              )}
            </Alert>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Typography color="text.secondary">No records are currently marked for deletion.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{isDocumentQueue ? 'Title' : 'Name'}</TableCell>
                      {isDocumentQueue && <TableCell>Type</TableCell>}
                      {isDocumentQueue && <TableCell>File</TableCell>}
                      <TableCell>Reference</TableCell>
                      <TableCell>Marked at</TableCell>
                      <TableCell>Scheduled purge</TableCell>
                      <TableCell>Marked by</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{cfg.label(row)}</TableCell>
                        {isDocumentQueue && (
                          <TableCell>{labelForDocumentType(row.document_type, documentTypes) || '—'}</TableCell>
                        )}
                        {isDocumentQueue && (
                          <TableCell
                            sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={row.file_name}
                          >
                            {row.file_name || '—'}
                          </TableCell>
                        )}
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {cfg.entity === 'contacts'
                            ? row.contact_code || row.id
                            : cfg.entity === 'accounts'
                              ? row.account_code || row.id
                              : row.document_code || row.id}
                        </TableCell>
                        <TableCell>{formatWhen(row.deletion_marked_at)}</TableCell>
                        <TableCell>{formatWhen(row.deletion_scheduled_for)}</TableCell>
                        <TableCell>
                          {row.deletionMarker
                            ? `${row.deletionMarker.first_name || ''} ${row.deletionMarker.last_name || ''}`.trim() ||
                              row.deletionMarker.email
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={isDocumentQueue ? 'Open Documents (Deleted filter)' : 'Open record'}>
                            <IconButton size="small" onClick={() => openRecord(row)}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revoke deletion — restore active (short view, then confirm)">
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={busyId === row.id}
                                onClick={() => setActionConfirm({ open: true, row, action: 'revoke' })}
                              >
                                <RevokeIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Archive — cancel purge, move to Archived Data (short view, then confirm)">
                            <span>
                              <IconButton
                                size="small"
                                color="warning"
                                disabled={busyId === row.id}
                                onClick={() => setActionConfirm({ open: true, row, action: 'archive' })}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Extend purge date">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={busyId === row.id}
                                onClick={() => {
                                  setExtendDays(7);
                                  setExtendOpen({ open: true, row });
                                }}
                              >
                                <ExtendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={actionConfirm.open}
        onClose={() => setActionConfirm({ open: false, row: null, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionConfirm.action === 'revoke' ? 'Revoke deletion' : 'Archive record'}
        </DialogTitle>
        <DialogContent>
          <DeletionShortView row={actionConfirm.row} cfg={cfg} isDocumentQueue={isDocumentQueue} documentTypes={documentTypes} />
          <Divider sx={{ my: 2 }} />
          {actionConfirm.action === 'revoke' ? (
            <Typography variant="body2" color="text.secondary">
              Confirm <strong>revoke</strong>: this record returns to an active state and is removed from the deletion queue.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Confirm <strong>archive</strong>: scheduled purge is cancelled and the record moves to Archived Data (eligible for
              backups from there).
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionConfirm({ open: false, row: null, action: null })}>Cancel</Button>
          <Button
            variant="contained"
            color={actionConfirm.action === 'revoke' ? 'success' : 'warning'}
            disabled={!actionConfirm.row || busyId === actionConfirm.row?.id}
            onClick={async () => {
              if (!actionConfirm.row || !actionConfirm.action) return;
              await postAction(actionConfirm.row, actionConfirm.action);
              setActionConfirm({ open: false, row: null, action: null });
            }}
          >
            {actionConfirm.action === 'revoke' ? 'Confirm revoke' : 'Confirm archive'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={extendOpen.open} onClose={() => setExtendOpen({ open: false, row: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Extend deletion schedule</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add days to the scheduled purge date for{' '}
            <strong>{extendOpen.row ? cfg.label(extendOpen.row) : ''}</strong>.
          </Typography>
          <TextField
            label="Additional days"
            type="number"
            fullWidth
            size="small"
            value={extendDays}
            onChange={(e) => setExtendDays(Math.max(1, Math.min(3650, parseInt(e.target.value, 10) || 1)))}
            inputProps={{ min: 1, max: 3650 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendOpen({ open: false, row: null })}>Cancel</Button>
          <Button variant="contained" onClick={submitExtend} disabled={busyId != null}>
            Extend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
